import { LoadingButton } from "@mui/lab"
import {
  Button,
  CardActions,
  CardContent,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
  Dialog,
  Autocomplete,
  Card,
  IconButton,
  Checkbox,
} from "@mui/material"
import {
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  DialogTransition,
  lowercaseFirstCharacter,
} from "../../services/utilities/utility"
import DialogTopBar from "../global/dialogTopBar"
import { useNavigate } from "react-router-dom"
import { MainContext } from "../../controllers/main"
import ISODatePicker from "../global/isoDatePicker"
import CheckInGroup from "../../models/checkInGroup"
import Team from "../../models/team"
import { MarketplaceContext } from "../../controllers/marketplace"
import { CheckInGroupsContext } from "../../controllers/checkInGroups"
import ChangeTeamDialog from "../teams/changeTeamDialog"
import CheckIn from "../../models/checkIn"
import { CheckInsContext } from "../../controllers/checkIns"
import { CheckInKind, CheckinDifficultyLevel } from "../../services/config/enum"
import CopyToClipboardButton from "../global/copyToClipboardButton"
import { OpenInNewRounded } from "@mui/icons-material"

const DuplicateCheckInGroupDialog = ({
  dialogOpen,
  setDialogOpen,
  handleDialogChange,
  checkInGroupToDuplicate,
  checkInGroupCheckInsListToDuplicate,
}: {
  dialogOpen: boolean
  setDialogOpen: Dispatch<SetStateAction<boolean>>
  handleDialogChange: MouseEventHandler<HTMLButtonElement>
  checkInGroupToDuplicate: CheckInGroup
  checkInGroupCheckInsListToDuplicate: CheckIn[]
}) => {
  const { setAnimation } = useContext(MainContext)
  const { setLoading, createCheckInGroup, createCheckInGroupDocument } =
    useContext(CheckInGroupsContext)
  const { createCheckIn, createCheckInDocument } = useContext(CheckInsContext)
  const { currenciesListLoading, currenciesList } =
    useContext(MarketplaceContext)
  const navigate = useNavigate()

  // prevent tab close
  const handleTabClose = useCallback((event: any) => {
    event.preventDefault()
    console.log("beforeunload event triggered")
    return (event.returnValue = "Are you sure you want to exit?")
  }, [])

  useEffect(() => {
    if (dialogOpen) {
      window.addEventListener("beforeunload", handleTabClose)
    } else {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [dialogOpen])

  // duration
  const [startDate, setStartDate] = useState<string>(
    checkInGroupToDuplicate.startDate
  )
  const [endDate, setEndDate] = useState<string>(
    checkInGroupToDuplicate.endDate
  )

  // configuration
  const [team, setTeam] = useState<Team | null>(checkInGroupToDuplicate.team)
  const [projectId, setProjectId] = useState<string>(
    checkInGroupToDuplicate.projectId
  )

  // checkins
  const [checkInsToInclude, setCheckInsToInclude] = useState<string[]>(
    checkInGroupCheckInsListToDuplicate.map((checkIn) => checkIn.id)
  )

  // change team dialog
  const [changeTeamDialogOpen, setChangeTeamDialogOpen] =
    useState<boolean>(false)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)

  // current slide and scrolling when changes
  const [currentSlide, setCurrentSlide] = useState<number>(0)

  useEffect(() => {
    setTimeout(() => {
      let slide = document.getElementById(currentSlide.toString())
      slide?.scrollIntoView({ behavior: "smooth" })
    }, 10)
  }, [currentSlide])

  // slides
  const slides = [
    {
      label: "Configuration",
      component: (
        <Stack spacing={2} style={{ maxHeight: 350, overflowY: "auto" }}>
          <Typography variant="h6" className="card-title">
            Configuration
          </Typography>
          <Stack direction="row" gap={2}>
            <ISODatePicker
              label="Start date"
              date={startDate}
              setDate={(newDate: string) => {
                setStartDate(newDate)
              }}
              hour={12}
            />
            <ISODatePicker
              label="End date"
              date={endDate}
              setDate={(newDate: string) => {
                setEndDate(newDate)
              }}
              hour={12}
              minDate={startDate}
            />
          </Stack>
          <TextField
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Stack direction="row" gap={1}>
                  <Button
                    sx={{ paddingInline: 3 }}
                    onClick={() => {
                      setChangeTeamDialogOpen(true)
                    }}
                  >
                    Change
                  </Button>
                  {team ? (
                    <Button
                      sx={{ paddingInline: 3 }}
                      onClick={() => {
                        setTeam(null)
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Stack>
              ),
            }}
            fullWidth
            label="Team"
            size="small"
            value={
              team
                ? team.document.items.find((item) => item.isDefault).title
                : "Global (App)"
            }
          />
          <Autocomplete
            fullWidth
            loading={currenciesListLoading}
            size="small"
            value={projectId}
            disablePortal
            options={
              currenciesList.length
                ? currenciesList.map((currency) => currency.projectId)
                : []
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Project ID (do not edit unless you are a developer)"
              />
            )}
            onChange={(_, newValue) => {
              setProjectId(newValue ? newValue.trim() : null)
            }}
            freeSolo
            autoSelect
          />
          <Typography variant="h6" className="card-title">
            Check-ins
          </Typography>
          {checkInGroupCheckInsListToDuplicate.length ? (
            <Stack gap={1}>
              {checkInGroupCheckInsListToDuplicate.map((checkIn) => {
                const isIncluded = checkInsToInclude.includes(checkIn.id)

                return (
                  <Stack
                    key={checkIn.id}
                    direction="row"
                    alignItems="center"
                    gap={2}
                  >
                    <Checkbox
                      checked={isIncluded}
                      onChange={(_, checked) => {
                        if (checked) {
                          setCheckInsToInclude((current) => [
                            ...current,
                            checkIn.id,
                          ])
                        } else {
                          setCheckInsToInclude((current) =>
                            current.filter((item) => item !== checkIn.id)
                          )
                        }
                      }}
                      style={{ width: 40, height: 40 }}
                    />
                    <Card
                      style={{
                        width: "100%",
                        opacity: isIncluded ? 1 : 0.5,
                        transition: "200ms",
                      }}
                    >
                      <CardContent
                        style={{
                          padding: 16,
                          paddingTop: 12,
                          paddingBottom: 12,
                        }}
                      >
                        <Stack direction="row" alignItems="center">
                          <Stack sx={{ gap: -5, flex: 1 }}>
                            <Typography
                              style={{
                                fontSize: 15,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "100%",
                              }}
                            >
                              {
                                checkIn.document.items.find(
                                  (item) => item.isDefault
                                ).title
                              }
                            </Typography>
                            <Stack
                              direction="row"
                              alignItems="center"
                              style={{ marginLeft: -3 }}
                            >
                              <CopyToClipboardButton
                                textToCopy={checkIn.id}
                                size="small"
                              />
                              <Typography
                                variant="caption"
                                style={{
                                  fontSize: 10,
                                  color: "gray",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {checkIn.id}
                              </Typography>
                            </Stack>
                          </Stack>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation()
                              window.open(`/checkins/${checkIn.id}`)
                            }}
                          >
                            <OpenInNewRounded style={{ fontSize: 18 }} />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                )
              })}
            </Stack>
          ) : (
            <Typography>No Check-ins</Typography>
          )}
        </Stack>
      ),
    },
  ]

  // reset dialog on close
  useEffect(() => {
    if (!dialogOpen) {
      setTimeout(() => {
        setStartDate(checkInGroupToDuplicate.startDate)
        setEndDate(checkInGroupToDuplicate.endDate)
        setTeam(checkInGroupToDuplicate.team)
        setProjectId(checkInGroupToDuplicate.projectId)
        setCheckInsToInclude(
          checkInGroupCheckInsListToDuplicate.map((checkIn) => checkIn.id)
        )
        setCurrentSlide(0)
      }, 100)
    }
  }, [dialogOpen, checkInGroupToDuplicate, checkInGroupCheckInsListToDuplicate])

  // discard dialog
  const [discardDialogOpen, setDiscardDialogOpen] = useState<boolean>(false)

  const handleDiscardDialogCancel = () => {
    setDiscardDialogOpen(false)
  }

  const handleDiscardDialogClose = (e: any) => {
    setDiscardDialogOpen(false)
    handleDialogChange(e)
  }

  // create checkin group
  const createNewCheckInGroup = async () => {
    setLoading(true)

    const input: {
      startDate: string
      endDate: string
      projectId: string
      teamId?: string
    } = {
      startDate: startDate.split("T")[0],
      endDate: endDate.split("T")[0],
      projectId,
      ...(team ? { teamId: team.id } : {}),
    }

    const result = await createCheckInGroup(input)

    if (result !== false) {
      const newCheckInGroupId = result as string

      const documentItemToDuplicate =
        checkInGroupToDuplicate.document.items.find((item) => item.isDefault)!

      const newBody = []
      documentItemToDuplicate.body.forEach((bodyItem: any) => {
        const { sliceType, ...rest } = bodyItem
        newBody.push({
          [lowercaseFirstCharacter(bodyItem.sliceType)]: {
            ...rest,
          },
        })
      })

      const documentInput: {
        parentId: string
        type: string
        checkinGroupDocumentItems: {
          body: object[]
          default: boolean
          lang: string
          title: string
          description: string
        }[]
      } = {
        parentId: newCheckInGroupId,
        type: "CheckinGroup",
        checkinGroupDocumentItems: [
          {
            body: newBody,
            default: true,
            lang: documentItemToDuplicate.lang,
            title: documentItemToDuplicate.title,
            description: documentItemToDuplicate.description,
          },
        ],
      }

      await createCheckInGroupDocument(documentInput)

      // create checkins
      for (const checkIn of checkInGroupCheckInsListToDuplicate.filter((item) =>
        checkInsToInclude.includes(item.id)
      )) {
        await createNewCheckIn(newCheckInGroupId, checkIn)
      }

      setDialogOpen(false)
      setAnimation(false)
      setLoading(false)
      setTimeout(() => {
        navigate("/checkingroups")
      }, 250)
    } else {
      setLoading(false)
    }
  }

  const createNewCheckIn = async (
    checkinGroupId: string,
    checkIn: CheckIn
  ): Promise<void> => {
    const input: {
      checkinGroupId: string
      kind: CheckInKind
      checkinDifficultyLevel: CheckinDifficultyLevel
      repeatable: boolean
      geoEnabled?: boolean
      geoRadius?: number
      geoCoordinates?: { lat: number; long: number }[]
    } = {
      checkinGroupId,
      kind: checkIn.kind,
      checkinDifficultyLevel: checkIn.difficultyLevel,
      repeatable: checkIn.repeatable,
      ...(checkIn.geoEnabled ? { geoEnabled: checkIn.geoEnabled } : {}),
      ...(checkIn.geoEnabled ? { geoRadius: checkIn.geoRadius } : {}),
      ...(checkIn.geoEnabled
        ? {
            geoCoordinates: checkIn.geoCoordinates.map((item) => {
              return {
                lat: item.lat,
                long: item.long,
              }
            }),
          }
        : {}),
    }

    const result = await createCheckIn(input)

    if (typeof result !== "boolean") {
      const newCheckIn = result

      const documentItemToDuplicate = checkIn.document.items.find(
        (item) => item.isDefault
      )!

      const newBody = []
      documentItemToDuplicate.body.forEach((bodyItem: any) => {
        const { sliceType, ...rest } = bodyItem
        newBody.push({
          [lowercaseFirstCharacter(bodyItem.sliceType)]: {
            ...rest,
          },
        })
      })

      const documentInput: {
        parentId: string
        type: string
        checkinDocumentItems: {
          body: object[]
          default: boolean
          lang: string
          title: string
          description: string
          zone?: string
        }[]
      } = {
        parentId: newCheckIn.id,
        type: "Checkin",
        checkinDocumentItems: [
          {
            body: newBody,
            default: true,
            lang: documentItemToDuplicate.lang,
            title: documentItemToDuplicate.title,
            description: documentItemToDuplicate.description,
            ...(documentItemToDuplicate.zone
              ? { zone: documentItemToDuplicate.zone }
              : {}),
          },
        ],
      }

      await createCheckInDocument(documentInput)
    }
  }

  return (
    <Dialog
      fullScreen
      open={dialogOpen}
      onClose={() => {
        setDiscardDialogOpen(true)
      }}
      TransitionComponent={DialogTransition}
    >
      <DialogTopBar
        handleDialogChange={() => {
          setDiscardDialogOpen(true)
        }}
        topBarText="Duplicate Check-in Group"
      />
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          variant="outlined"
          style={{
            width: "70%",
            minWidth: 345,
            maxWidth: 850,
            height: 500,
            position: "relative",
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            style={{ paddingTop: 16 }}
          >
            Duplicate Check-in Group
          </Typography>
          <div
            style={{
              minWidth: 345,
              maxWidth: 850,
              height: "auto",
            }}
          >
            {slides.map((slide, index) => (
              <div
                key={index}
                id={index.toString()}
                style={{ minWidth: "100%", scrollSnapAlign: "center" }}
              >
                <CardContent>{slide.component}</CardContent>
              </div>
            ))}
          </div>
          <CardActions
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              padding: 16,
              width: "100%",
            }}
          >
            <div style={{ width: "50%" }}>
              <Button
                variant="outlined"
                onMouseDown={() => {
                  if (!slides[currentSlide - 1]) {
                    setDiscardDialogOpen(true)
                  } else {
                    setCurrentSlide(currentSlide - 1)
                  }
                }}
              >
                Close
              </Button>
            </div>
            <div
              style={{
                width: "50%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <LoadingButton
                variant="contained"
                disabled={
                  (currentSlide === 0 && !startDate) ||
                  (currentSlide === 0 && !endDate) ||
                  (currentSlide === 0 && !projectId)
                }
                onMouseDown={
                  !slides[currentSlide + 1]
                    ? createNewCheckInGroup
                    : () => {
                        setCurrentSlide(currentSlide + 1)
                      }
                }
              >
                Duplicate
              </LoadingButton>
            </div>
          </CardActions>
        </Paper>
      </div>
      <Dialog
        open={discardDialogOpen}
        onClose={handleDiscardDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to close this window?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            All entered data will be lost
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardDialogCancel}>Cancel</Button>
          <Button onClick={handleDiscardDialogClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* change team dialog */}
      <ChangeTeamDialog
        open={changeTeamDialogOpen}
        setOpen={setChangeTeamDialogOpen}
        selectedTeam={selectedTeam}
        setSelectedTeam={setSelectedTeam}
        setTeam={setTeam}
      />
    </Dialog>
  )
}

export default DuplicateCheckInGroupDialog
